import React, {useEffect, useState} from 'react';
import {useNavigate, useLocation} from "react-router-dom";
import api from "../../api";
import {Button, Col, DatePicker, Drawer, Form, Input, InputNumber, message, Popconfirm, Row, Table, Tabs} from "antd";
import {EditOutlined, DeleteOutlined} from '@ant-design/icons';
import dayjs from 'dayjs';

export default function AdminAdSpends() {
    const navigate = useNavigate();
    const location = useLocation();
    const [adSpends, setAdSpends] = useState([]);
    const [loading, setLoading] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [editingAdSpend, setEditingAdSpend] = useState(null);
    const [form] = Form.useForm();

    // Determine active tab based on route
    const getActiveTab = () => {
        if (location.pathname === '/admin/suppliers') return 'suppliers';
        if (location.pathname === '/admin/icons') return 'icons';
        if (location.pathname === '/admin/users') return 'users';
        if (location.pathname === '/admin/blog') return 'blog';
        if (location.pathname === '/admin/ad-spends') return 'ad-spends';
        if (location.pathname === '/admin/affiliates') return 'affiliates';
        return 'stats';
    };

    const handleTabChange = (key) => {
        switch(key) {
            case 'stats':
                navigate('/admin');
                break;
            case 'suppliers':
                navigate('/admin/suppliers');
                break;
            case 'icons':
                navigate('/admin/icons');
                break;
            case 'users':
                navigate('/admin/users');
                break;
            case 'blog':
                navigate('/admin/blog');
                break;
            case 'ad-spends':
                navigate('/admin/ad-spends');
                break;
            case 'affiliates':
                navigate('/admin/affiliates');
                break;
            default:
                navigate('/admin');
        }
    };

    useEffect(() => {
        loadAdSpends();
    }, []);

    const loadAdSpends = () => {
        setLoading(true);
        api.getAdSpends()
            .then(setAdSpends)
            .finally(() => setLoading(false));
    };

    const handleCreate = () => {
        setEditingAdSpend(null);
        form.resetFields();
        setDrawerOpen(true);
    };

    const handleEdit = (adSpend) => {
        setEditingAdSpend(adSpend);
        form.setFieldsValue({
            date: dayjs(adSpend.date),
            utm_medium: adSpend.utm_medium,
            amount: adSpend.amount,
        });
        setDrawerOpen(true);
    };

    const handleDelete = (id) => {
        api.deleteAdSpend(id)
            .then(() => {
                message.success('Ad spend verwijderd');
                loadAdSpends();
            })
            .catch(() => message.error('Fout bij verwijderen'));
    };

    const handleSubmit = () => {
        form.validateFields().then((values) => {
            const data = {
                date: values.date.format('YYYY-MM-DD'),
                utm_medium: values.utm_medium,
                amount: values.amount,
            };

            const promise = editingAdSpend
                ? api.updateAdSpend(editingAdSpend.id, data)
                : api.createAdSpend(data);

            promise
                .then(() => {
                    message.success(editingAdSpend ? 'Ad spend bijgewerkt' : 'Ad spend aangemaakt');
                    setDrawerOpen(false);
                    form.resetFields();
                    loadAdSpends();
                })
                .catch(() => message.error('Fout bij opslaan'));
        });
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 80,
        },
        {
            title: 'Datum',
            dataIndex: 'date',
            key: 'date',
            render: (date) => dayjs(date).format('YYYY-MM-DD'),
        },
        {
            title: 'UTM Medium',
            dataIndex: 'utm_medium',
            key: 'utm_medium',
        },
        {
            title: 'Bedrag',
            dataIndex: 'amount',
            key: 'amount',
            render: (amount) => `€${amount.toFixed(2)}`,
        },
        {
            title: 'Acties',
            key: 'actions',
            width: 120,
            render: (_, record) => (
                <Row gutter={8}>
                    <Col>
                        <Button
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => handleEdit(record)}
                        />
                    </Col>
                    <Col>
                        <Popconfirm
                            title="Weet je zeker dat je deze ad spend wilt verwijderen?"
                            onConfirm={() => handleDelete(record.id)}
                            okText="Ja"
                            cancelText="Nee"
                        >
                            <Button
                                size="small"
                                danger
                                icon={<DeleteOutlined />}
                            />
                        </Popconfirm>
                    </Col>
                </Row>
            ),
        },
    ];

    return (
        <>
            <Drawer
                title={editingAdSpend ? 'Ad Spend bewerken' : 'Nieuwe ad spend'}
                width={600}
                open={drawerOpen}
                onClose={() => {
                    setDrawerOpen(false);
                    form.resetFields();
                }}
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        name="date"
                        label="Datum"
                        rules={[{ required: true, message: 'Datum is verplicht' }]}
                    >
                        <DatePicker format="YYYY-MM-DD" style={{width: '100%'}} />
                    </Form.Item>

                    <Form.Item
                        name="utm_medium"
                        label="UTM Medium"
                        rules={[{ required: true, message: 'UTM Medium is verplicht' }]}
                    >
                        <Input placeholder="cpc, social, email, etc." />
                    </Form.Item>

                    <Form.Item
                        name="amount"
                        label="Bedrag (€)"
                        rules={[{ required: true, message: 'Bedrag is verplicht' }]}
                    >
                        <InputNumber
                            min={0}
                            step={0.01}
                            style={{width: '100%'}}
                            placeholder="0.00"
                        />
                    </Form.Item>

                    <Form.Item>
                        <Row gutter={8}>
                            <Col>
                                <Button type="primary" onClick={handleSubmit}>
                                    {editingAdSpend ? 'Bijwerken' : 'Aanmaken'}
                                </Button>
                            </Col>
                        </Row>
                    </Form.Item>
                </Form>
            </Drawer>

            <h1>Admin</h1>

            <Tabs
                activeKey={getActiveTab()}
                onChange={handleTabChange}
                items={[
                    {
                        label: 'Stats',
                        key: 'stats',
                        children: null
                    },
                    {
                        label: 'Suppliers',
                        key: 'suppliers',
                        children: null
                    },
                    {
                        label: 'Icons',
                        key: 'icons',
                        children: null
                    },
                    {
                        label: 'Users',
                        key: 'users',
                        children: null
                    },
                    {
                        label: 'Blog Posts',
                        key: 'blog',
                        children: null
                    },
                    {
                        label: 'Affiliates',
                        key: 'affiliates',
                        children: null
                    },
                    {
                        label: 'Ad Spends',
                        key: 'ad-spends',
                        children: (
                            <>
                                <Row style={{marginBottom: 16}}>
                                    <Col span={12}>
                                        <h2>Ad Spends</h2>
                                    </Col>
                                    <Col span={12} style={{textAlign: 'right'}}>
                                        <Button type="primary" onClick={handleCreate}>
                                            Nieuwe ad spend
                                        </Button>
                                    </Col>
                                </Row>

                                <Table
                                    dataSource={adSpends}
                                    columns={columns}
                                    rowKey="id"
                                    loading={loading}
                                    pagination={false}
                                />
                            </>
                        )
                    },
                ]}
            />
        </>
    );
}
